import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { authService } from "../services/api";
import EmployeePayrollSummary from "./payroll/EmployeePayrollSummary";
import {
  EmployeePayslipsPanel,
  EmployeeForm16Panel,
} from "./payroll/EmployeePayrollDocsPanels";
import EmployeeFlexiReimbursementPanel from "./payroll/EmployeeFlexiReimbursementPanel";

const TABS = [
  { id: "summary", label: "My Payroll Summary" },
  { id: "payslips", label: "Payslips" },
  { id: "reimbursement", label: "Reimbursement" },
  { id: "form16", label: "Form-16" },
];

function resolveTab(raw) {
  const key = String(raw || "")
    .toLowerCase()
    .replace(/\s+/g, "-");
  if (key === "payslips" || key === "payslip") return "payslips";
  if (key === "reimbursement" || key === "flexi" || key === "flexi-benefits")
    return "reimbursement";
  if (key === "form16" || key === "form-16" || key === "form_16") return "form16";
  if (
    key === "summary" ||
    key === "my-payroll-summary" ||
    key === "investment-declaration"
  ) {
    return "summary";
  }
  return "summary";
}

/**
 * Employee → Payroll hub.
 * Shell uses core surface / ink tokens only — no amber/tint washes.
 */
export default function EmployeePayroll() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() =>
    resolveTab(searchParams.get("tab"))
  );
  const [employeeId, setEmployeeId] = useState(null);
  const reimbMode = (() => {
    const m = String(searchParams.get("mode") || "hub").toLowerCase();
    return m === "declare" || m === "claim" ? m : "hub";
  })();

  useEffect(() => {
    setActiveTab(resolveTab(searchParams.get("tab")));
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const userRes = await authService.getCurrentUser();
        const id = userRes?.data?.employeeId ?? userRes?.employeeId ?? null;
        if (!cancelled) setEmployeeId(id);
      } catch {
        if (!cancelled) setEmployeeId(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectTab = (id) => {
    setActiveTab(id);
    setSearchParams(id === "summary" ? {} : { tab: id }, { replace: true });
  };

  return (
    <div className="p-4 lg:p-6">
      <div className="mx-auto max-w-8xl overflow-hidden rounded-[18px] border border-[hsl(var(--hrms-line))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)]">
        <div className="border-b border-[hsl(var(--hrms-line))] bg-[hsl(var(--ds-surface))] px-4 py-4 sm:px-6">
          <h2 className="text-lg font-semibold text-[hsl(var(--ds-fg))]">Payroll</h2>
          <p className="mt-0.5 text-sm text-[hsl(var(--ds-muted-fg))]">
            Emoluments, statutory, payslips, and Form-16. Opt-in / claims from{" "}
            <span className="font-medium text-[hsl(var(--ds-fg))]">
              Quick Panel → Opt-in / Claim
            </span>
            .
          </p>
        </div>

        <div className="flex flex-wrap gap-1 border-b border-[hsl(var(--hrms-line))] bg-[hsl(var(--ds-surface))] px-2 pt-2 sm:px-4">
          {TABS.map((tab) => {
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => selectTab(tab.id)}
                className={`rounded-t-[11px] border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                  active
                    ? "border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-primary)/0.08)] text-[hsl(var(--ds-primary))]"
                    : "border-transparent text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))] hover:text-[hsl(var(--ds-fg))]"
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="bg-[hsl(var(--ds-bg))] p-4 sm:p-6">
          {activeTab === "summary" && (
            <EmployeePayrollSummary employeeId={employeeId} />
          )}
          {activeTab === "payslips" && <EmployeePayslipsPanel />}
          {activeTab === "reimbursement" && (
            <EmployeeFlexiReimbursementPanel
              employeeId={employeeId}
              mode={reimbMode}
            />
          )}
          {activeTab === "form16" && <EmployeeForm16Panel />}
        </div>
      </div>
    </div>
  );
}
